import { BreadcrumbsProps } from '@bridebook/web/app-basic/components/breadcrumbs/breadcrumbs.types';
import { getSupplierGenericProperties } from '@bridebook/web/app-basic/lib/analytics/helper';
import { clickedOnLoggedOutPage } from '@bridebook/web/app-basic/lib/analytics/tracking-actions/logged-out-page-click';
import { getActualCategory } from '@bridebook/web/app-basic/lib/search/selectors';
import { getSupplier, getSupplierTier } from '@bridebook/web/app-basic/lib/supplier/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useRouter } from 'next/router';
import { FC, memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { IBreadcrumbItem } from '@bridebook/web/app-shared/lib/types';

interface IProps extends BreadcrumbsProps {
  breadcrumbsData: Array<IBreadcrumbItem>;
}

const addLocale = (path: string, marketPrefix: string) => {
  if (!path.startsWith('/') || path.startsWith(`/${marketPrefix}`)) {
    return path;
  }

  return `/${marketPrefix}${path}`;
};

const getBreadcrumbHref = (
  path: string | undefined,
  shouldReload: boolean,
  reloadPath: string,
  marketPrefix: string,
) => {
  if (shouldReload || !path) {
    return reloadPath;
  }

  return addLocale(path, marketPrefix);
};

const BreadcrumbsComponent: FC<IProps> = ({
  center = true,
  reloadOnLast = false,
  breadcrumbsData,
}) => {
  const router = useRouter();
  const market = useMarket();
  const supplier = useSelector(getSupplier);
  const supplierTier = useSelector(getSupplierTier);
  const actualCategory = useSelector(getActualCategory);
  const { country } = useMarket();
  const dispatch = useDispatch();

  const breadcrumbsItems = useMemo(() => {
    let firstVisibleItemFound = false;

    return breadcrumbsData.map((item, index) => {
      let isHidden = item.id !== 'supplier-category' && item.id !== 'county';
      if (!breadcrumbsData.some((i) => i.id === 'county') && item.id === 'town') {
        isHidden = false;
      }

      // Can't rely on :first-child because earlier crumbs may be hidden yet still in the DOM.
      const isFirst = !isHidden && !firstVisibleItemFound;
      if (isFirst) {
        firstVisibleItemFound = true;
      }

      const shouldReload = reloadOnLast && breadcrumbsData.length - 1 === index;

      return {
        ...item,
        href: getBreadcrumbHref(item.path, shouldReload, router.asPath, market.prefix),
        reload: shouldReload,
        isHidden,
        isFirst,
      };
    });
  }, [breadcrumbsData, market.prefix, reloadOnLast, router.asPath]);

  const handleAnalytics = useCallback(() => {
    dispatch(
      clickedOnLoggedOutPage({
        clickedLocation: supplier ? 'supplierProfile' : 'search',
        clickedSection: 'breadcrumb',
        cta: 'breadcrumb',
        ...(supplier
          ? getSupplierGenericProperties({ supplier, supplierTier })
          : {
              supplierCategory: actualCategory,
              supplierCountryCode: country,
            }),
      }),
    );
  }, [actualCategory, country, dispatch, supplier, supplierTier]);

  const handleBreadcrumbClick = useCallback(
    (breadcrumbsItem: (typeof breadcrumbsItems)[0]) => () => {
      handleAnalytics();
      if (breadcrumbsItem.reload) {
        router.reload();
      }
    },
    [handleAnalytics, router],
  );

  return (
    <div className={center ? 'bcbr bcbr-centered' : 'bcbr'}>
      <div>
        <ul itemScope itemType="https://schema.org/BreadcrumbList">
          {breadcrumbsItems.map((breadcrumbsItem, i) => (
            <li
              key={`sbr-${breadcrumbsItem.id}`}
              className={[
                breadcrumbsItem.isHidden && 'bcbr-crumb-hidden',
                !breadcrumbsItem.isHidden && !breadcrumbsItem.isFirst && 'bcbr-crumb-sep',
              ]
                .filter(Boolean)
                .join(' ')}
              itemScope
              itemProp="itemListElement"
              itemType="https://schema.org/ListItem">
              <a
                itemProp="item"
                href={breadcrumbsItem.href}
                onClick={handleBreadcrumbClick(breadcrumbsItem)}>
                <span itemProp="name">{breadcrumbsItem.name}</span>
              </a>
              <meta itemProp="position" content={`${i + 1}`} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export const Breadcrumbs = memo(BreadcrumbsComponent);
